/**
 * Event Types Registry
 *
 * Centralised string constants for every SSE event type in the system.
 * Grouped by owning bounded context (namespace).
 *
 * Usage:
 *   import { EVENT_TYPES } from '@/shared/infrastructure/events/eventTypes';
 *   getEventBus().emit({ type: EVENT_TYPES.SUBSCRIPTION.BALANCE_UPDATED, ... });
 */

export const EVENT_TYPES = {
  // ─── Subscriptions ──────────────────────────────────────────────────
  SUBSCRIPTION: {
    BALANCE_UPDATED: 'subscription.balance.updated',
  },
  TOKENS: {
    INSUFFICIENT: 'tokens.insufficient',
  },

  // ─── Family ─────────────────────────────────────────────────────────
  FAMILY: {
    MEMBER_ADDED: 'family.member.added',
    MEMBER_UPDATED: 'family.member.updated',
    MEMBER_REMOVED: 'family.member.removed',
    SETTINGS_UPDATED: 'family.settings.updated',
    INVITATION_CREATED: 'family.invitation.created',
    INVITATION_USED: 'family.invitation.used',
    PIN_CHANGED: 'family.pin.changed',
  },

  // ─── Stories ────────────────────────────────────────────────────────
  STORIES: {
    CREATED: 'stories.created',
    UPDATED: 'stories.updated',
    DELETED: 'stories.deleted',
    GENERATION_DONE: 'stories.generation.done',
    GENERATION_FAILED: 'stories.generation.failed',
    AUDIO_DONE: 'stories.audio.done',
    ASSEMBLY_DONE: 'stories.assembly.done',
    ASSEMBLY_FAILED: 'stories.assembly.failed',
    PROGRESS: 'stories.progress',
    BLOCK_UPDATED: 'stories.block.updated',
    /** The story's stress marks are ready (ru/uk) — one event for every block. */
    ACCENTED: 'stories.accented',
    /** A block's text is being rewritten / has been rewritten (the card says so). */
    BLOCK_REWRITING: 'stories.block.rewriting',
    CHARACTERS_EXTRACTED: 'stories.characters.extracted',
  },

  // ─── Books ──────────────────────────────────────────────────────────
  BOOKS: {
    CREATED: 'books.created',
    UPDATED: 'books.updated',
    DELETED: 'books.deleted',
    PAGE_IMAGE_COMPLETED: 'books.page.image-completed',
    PAGE_IMAGE_FAILED: 'books.page.image-failed',
    COVER_COMPLETED: 'books.cover.completed',
    COVER_FAILED: 'books.cover.failed',
    CHARACTER_REFERENCE_COMPLETED: 'books.character.reference-completed',
    CHARACTER_REFERENCE_FAILED: 'books.character.reference-failed',
  },

  // ─── Characters ─────────────────────────────────────────────────────
  CHARACTERS: {
    CREATED: 'character.created',
    UPDATED: 'character.updated',
    DELETED: 'character.deleted',
    PORTRAIT_COMPLETED: 'character.portrait.completed',
    PORTRAIT_FAILED: 'character.portrait.failed',
  },

  // ─── Media ──────────────────────────────────────────────────────────
  MEDIA: {
    EXPORT_PROGRESS: 'media.export.progress',
    EXPORT_COMPLETED: 'media.export.completed',
    EXPORT_FAILED: 'media.export.failed',
  },

  // ─── Audio Stories ──────────────────────────────────────────────────
  AUDIO_STORIES: {
    CREATED: 'audio-stories.created',
    DELETED: 'audio-stories.deleted',
    BLOCK_AUDIO_GENERATING: 'audio-stories.block.audio-generating',
    BLOCK_AUDIO_COMPLETED: 'audio-stories.block.audio-completed',
    BLOCK_AUDIO_FAILED: 'audio-stories.block.audio-failed',
    BLOCK_RECORDING_UPLOADED: 'audio-stories.block.recording-uploaded',
    ASSEMBLY_STARTED: 'audio-stories.assembly.started',
    ASSEMBLY_DONE: 'audio-stories.assembly.done',
    VOICE_SETTINGS_UPDATED: 'audio-stories.voice-settings.updated',
  },

  // ─── Voice ──────────────────────────────────────────────────────────
  VOICE: {
    CLONE_COMPLETED: 'voice.clone.completed',
    CLONE_FAILED: 'voice.clone.failed',
    DELETED: 'voice.deleted',
    PREVIEW_COMPLETED: 'voice.preview.completed',
    PREVIEW_FAILED: 'voice.preview.failed',
  },

  // ─── Content ────────────────────────────────────────────────────────
  CONTENT: {
    PLAN_UPDATED: 'content.plan.updated',
    TASK_UPDATED: 'content.task.updated',
  },

  // ─── Pipeline v2 (order plan) ───────────────────────────────────────
  PIPELINE: {
    /** a layer of a v2 order moved, or a step of its run was journalled */
    LAYER_UPDATED: 'pipeline.layer.updated',
    ORDER_NOTICE: 'pipeline.order.notice',
  },

  // ─── Printing ───────────────────────────────────────────────────────
  PRINTING: {
    ORDER_STATUS_CHANGED: 'printing.order.status-changed',
  },

  // ─── Workflows (owlcat_jobs engine, via WorkflowEventBridge) ────────
  WORKFLOW: {
    RUN_UPDATED: 'workflow.run.updated',
  },
} as const;
